import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { formatDate } from '@utils/format';

import { useAdminListPermissions } from 'generated/admin.generated';

const messages = defineMessages({
  name: {
    id: 'admin.permissions.name',
    defaultMessage: 'Name',
  },
  scope: {
    id: 'admin.permissions.scope',
    defaultMessage: 'Scope',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  noData: {
    id: 'admin.permissions.noData',
    defaultMessage: 'No permissions',
  },
  create: {
    id: 'admin.permissions.create',
    defaultMessage: 'New Permission',
  },
});

interface RowData {
  id: string;
  name: string;
  scope: string;
  created_at: string;
  updated_at: string;
}

function PermissionsList() {
  const { formatMessage } = useIntl();
  const { data, loading } = useAdminListPermissions({
    variables: { input: { scope: null } },
  });

  const tableData = React.useMemo(
    () =>
      (data?.listPermissions ?? []).map<RowData>((permission) => {
        return {
          id: permission.permission_id,
          name: permission.permission_name,
          scope: permission.permission_scope,
          updated_at: permission.updated_at,
          created_at: permission.created_at,
        };
      }, []),
    [data]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { id } = tableData[dataIndex];
    navigate(`${ROUTES.ADMIN_MANAGE_PERMISSIONS}${id}`);
  };

  const handleCreate = () => {
    navigate(ROUTES.ADMIN_MANAGE_PERMISSIONS_CREATE);
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'scope',
            className: 'small',
            label: formatMessage(messages.scope),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'name',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.createdAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['created_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
          {
            name: 'updated_at',
            className: 'date',
            label: formatMessage(messages.updatedAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['updated_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
        ]}
        data={tableData}
        options={{
          search: true,
          sort: true,
          filter: true,
          sortOrder: {
            name: 'scope',
            direction: 'asc',
          },
          onRowClick: handleRowClick,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData),
            },
          },
          customToolbar: () => (
            <RoundedButton
              autoSize
              iconName="plus"
              margin={0}
              onClick={handleCreate}
            >
              {formatMessage(messages.create)}
            </RoundedButton>
          ),
        }}
      />
    </ContentLoader>
  );
}

export default PermissionsList;
